/**
 * Build palette — the homestead piece picker. Opened with E in build mode: an
 * inventory-style grid of every catalog piece, grouped by category, showing
 * material costs and dimming what the player can't afford. Clicking a cell
 * selects it and CLOSES the palette (the hammer now holds that piece).
 *
 * Self-contained inline styles (no style.css coupling) matching the game's
 * dark-panel + cyan-accent look. `onPick(t)` receives the catalog index;
 * `onClose` re-grabs pointer lock (the journal/travel-log convention).
 */
import {
  BUILD_PIECE_GROUPS,
  PIECE_CATALOG,
  pieceCatalogCost,
  ITEMS,
} from "@hyperlight/shared";

const ACCENT = "#2fb8df";

export class BuildPalette {
  visible = false;
  private el: HTMLElement;

  constructor(private handlers: { onPick: (t: number) => void; onClose: () => void }) {
    this.el = document.createElement("div");
    this.el.id = "build-palette";
    this.el.style.cssText =
      "display:none;position:fixed;inset:0;z-index:60;align-items:center;justify-content:center;"
      + "background:rgba(4,8,12,0.72);font-family:'Segoe UI',system-ui,sans-serif;";
    document.body.appendChild(this.el);
    window.addEventListener("keydown", (e) => {
      if (!this.visible) return;
      if (e.code === "Escape" || e.code === "KeyE" || e.code === "KeyH") {
        e.preventDefault();
        e.stopImmediatePropagation(); // an H here must not ALSO exit build mode
        this.close();
      }
    }, { capture: true });
  }

  /** Render + show. `selected` = current catalog index; `afford[t]` = buyable now. */
  open(selected: number, afford: readonly boolean[], recommended: readonly string[] = []): void {
    this.visible = true;
    this.el.style.display = "flex";
    let sections = "";
    for (const group of BUILD_PIECE_GROUPS) {
      let cells = "";
      for (let t = 0; t < PIECE_CATALOG.length; t++) {
        const e = PIECE_CATALOG[t];
        if (e.group !== group) continue;
        const cost = (pieceCatalogCost(t) ?? [])
          .map((s) => `${s.n}× ${ITEMS[s.id]?.name ?? s.id}`)
          .join(" · ");
        const ok = afford[t] !== false;
        const isSel = t === selected;
        const isRecommended = recommended.includes(e.id);
        cells += `<div class="bp-cell" data-t="${t}" style="
            width:118px;min-height:74px;padding:8px 9px;border-radius:4px;cursor:pointer;
            background:${isSel ? "rgba(47,184,223,0.18)" : "rgba(255,255,255,0.05)"};
            border:1px solid ${isRecommended ? "#ffcf5a" : isSel ? ACCENT : "rgba(255,255,255,0.14)"};
            opacity:${ok ? 1 : 0.38};
          ">
          <div style="color:#e8f2f8;font-size:12px;font-weight:600;letter-spacing:0.04em;">${e.name.toUpperCase()}${isRecommended ? ' <span style="color:#ffcf5a;font-size:9px;">· NEXT</span>' : ""}</div>
          <div style="color:${ok ? "#9fb4c2" : "#c66"};font-size:10px;margin-top:5px;line-height:1.5;">${cost}</div>
        </div>`;
      }
      if (!cells) continue;
      sections += `<div style="margin-bottom:14px;">
        <div style="color:${ACCENT};font-size:11px;letter-spacing:0.22em;margin-bottom:7px;">${group.toUpperCase()}</div>
        <div style="display:flex;flex-wrap:wrap;gap:8px;">${cells}</div>
      </div>`;
    }
    this.el.innerHTML = `
      <div style="max-width:840px;max-height:82vh;overflow-y:auto;padding:22px 26px;border-radius:6px;
          background:rgba(10,16,22,0.96);border:1px solid rgba(47,184,223,0.35);box-shadow:0 0 40px rgba(0,0,0,0.6);">
        <div style="color:#e8f2f8;font-size:15px;letter-spacing:0.28em;margin-bottom:4px;">BUILD PIECES</div>
        <div style="color:#7c8f9c;font-size:11px;margin-bottom:16px;">CLICK TO SELECT · E / ESC TO CLOSE</div>
        ${sections}
      </div>`;
    // One listener via delegation; a pick selects AND closes (the hammer holds it).
    this.el.addEventListener("click", (ev) => {
      const cell = (ev.target as HTMLElement).closest?.(".bp-cell") as HTMLElement | null;
      if (cell?.dataset.t !== undefined) {
        this.handlers.onPick(Number(cell.dataset.t));
        this.close();
      } else if (ev.target === this.el) {
        this.close(); // click the dim backdrop to cancel
      }
    }, { once: false });
  }

  close(): void {
    if (!this.visible) return;
    this.visible = false;
    this.el.style.display = "none";
    this.el.innerHTML = "";
    this.handlers.onClose();
  }
}
